import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatFlatPayload } from "mocks/express/response.transforms";
import { fetchDeliveryDriversByHub } from "Express/redux/expressSlice";
import { AppState } from "config/redux/reducers";
import { debounce } from "lodash";
import { ASSIGNEE_TYPE } from "constants/ExpressConstants";

interface PaginatedAssigneeListParams {
    hubCode: string;
    deliveryType: string;
    assigneeType: string;
    enabled?: boolean;
}

export function usePaginatedAssigneeList({ hubCode, deliveryType, assigneeType, enabled = true }: PaginatedAssigneeListParams) {
    const dispatch = useDispatch();
    const { countryCode } = useSelector((state: AppState) => state.common);
    const assigneeListByHub = useSelector((state: AppState) =>
        assigneeType === ASSIGNEE_TYPE.DRIVER
            ? state.expressDashboard.driversListByHub
            : state.expressDashboard.helperListByHub
    );
    const [assigneeList, setAssigneeList] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(0);
    const [search, setSearch] = useState("");

    const fetchAssignees = useCallback((searchText = "", loadMore = false) => {
        if (!enabled) return;
        setLoading(true);
        const payloadFilters = { hubCode, deliveryType, assigneeType };
        const options = { countryISOCode: countryCode, searchAttribute: searchText, pageNumber: loadMore ? page + 1 : 0 };
        const payload = formatFlatPayload(payloadFilters, assigneeListByHub, options, loadMore);
        dispatch(fetchDeliveryDriversByHub({ payload }));
    }, [dispatch, countryCode, hubCode, deliveryType, assigneeType, page, enabled, assigneeListByHub]);

    useEffect(() => {
        if (assigneeListByHub?.elements) {
            setAssigneeList(assigneeListByHub.elements);
            setPage(assigneeListByHub.pageNumber || 0);
            setHasMore((assigneeListByHub.pageNumber+1 || 0) < (assigneeListByHub.totalPages || 1));
            setLoading(false);
        }
     
    }, [assigneeListByHub]);

    const handleAssigneeSearch = useCallback(
        debounce((searchText: string) => {
            setSearch(searchText);
            setPage(0);
            if (searchText.length >= 3 || searchText.length === 0) {
                fetchAssignees(searchText, false);
            }
        }, 1000),
        [fetchAssignees]
    );

    const handleLoadMore = useCallback(
        debounce(() => {
            fetchAssignees(search, true);
        }, 300),
        [fetchAssignees, search]
    );

    useEffect(() => {
        if (enabled) fetchAssignees();
    }, [enabled]);

    return {
        assigneeList,
        handleAssigneeSearch,
        handleLoadMore,
        hasMore,
        assigneeLoading: loading,
    };
}